import type {ContentReviewDto} from '@devmil/contracts';
import {useEffect, useId, useState} from 'react';

import {usePanel} from '../../app/PanelProvider.js';
import {formatTimeOfDay, KIND_LABEL} from '../../lib/format.js';
import {CarouselViewer} from './CarouselViewer.js';
import {DecisionForm} from './DecisionForm.js';
import {EditorialOpinion} from './EditorialOpinion.js';
import {GateList} from './GateList.js';
import {MediaOpinion} from './MediaOpinion.js';
import {MediaPreview} from './MediaPreview.js';

export const ReviewPanel = ({
  piece,
  changeBoxOpen,
  onOpenChangeBox,
  onCloseChangeBox,
}: {
  piece: ContentReviewDto;
  changeBoxOpen: boolean;
  onOpenChangeBox: () => void;
  onCloseChangeBox: () => void;
}) => {
  const headingId = useId();
  const {dataSource} = usePanel();

  /**
   * O RASCUNHO que o pedido de ajuste de um slide deixa no comentário.
   *
   * Ele vive aqui, e não dentro do `CarouselViewer`, porque quem tem o campo de
   * comentário é o `DecisionForm`, que é irmão dele. Um estado dentro do
   * navegador de slides teria que atravessar dois componentes para chegar lá.
   *
   * Trocar de peça o descarta: um rascunho escrito sobre o slide 4 de um
   * carrossel não pode aparecer no comentário de outra peça.
   */
  const [rascunho, setRascunho] = useState<string | null>(null);
  useEffect(() => {
    setRascunho(null);
  }, [piece.contentVersionId]);

  /*
   * O endereço só é montado para o que o SERVIDOR disse existir.
   *
   * As duas condições precisam ser verdadeiras: a medição do snapshot
   * (`piece.media`) e a fonte ter endereço para dar (a simulada devolve
   * `null`). Sem a primeira, a tela pediria mídia de peça em rascunho e
   * mostraria imagem quebrada na hora de aprovar; sem a segunda, a revisão
   * visual local passaria a bater numa rota que não existe no modo fixture.
   */
  const coverUrl = piece.media.cover
    ? dataSource.mediaUrl(piece.contentVersionId, 'cover')
    : null;
  const videoUrl = piece.media.video
    ? dataSource.mediaUrl(piece.contentVersionId, 'media')
    : null;

  /*
   * Os SLIDES, um endereço por posição MEDIDA pelo servidor.
   *
   * A lista sai de `piece.media.slides`, que é a contagem do que existe no
   * volume, e não de `CAROUSEL_SLIDES`: pedir sete endereços numa peça que
   * renderizou cinco poria dois quadros quebrados na tela de aprovação, e
   * esconderia justamente o que o dono precisa ver, que é a peça incompleta.
   */
  const slides = Array.from({length: piece.media.slides}, (_, indice) => ({
    position: indice + 1,
    url: dataSource.mediaUrl(piece.contentVersionId, {slide: indice + 1}),
  }));

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Revisão individual</h2>
        <span className="dm-illustrative">
          {formatTimeOfDay(piece.scheduledAt)}
        </span>
      </div>
      <div className="dm-review-grid">
        {piece.kind === 'carousel' ? (
          <CarouselViewer
            contentVersionId={piece.contentVersionId}
            slides={slides}
            preview={piece.preview}
            onPedirAjuste={(posicao) => {
              // O rascunho ANCORA o pedido no slide, e o resto do texto é do
              // dono. Mandar o pedido inteiro pronto seria escrever a decisão
              // por ele, que é exatamente o que esta tela não faz.
              setRascunho(`Slide ${posicao}: `);
              onOpenChangeBox();
            }}
          />
        ) : (
          <MediaPreview preview={piece.preview} media={{coverUrl, videoUrl}} />
        )}
        <div className="dm-review-info">
          <span className="dm-kicker">
            {`${KIND_LABEL[piece.kind]} · ${piece.series.name}`}
          </span>
          <h3>{piece.title}</h3>
          <p className="dm-caption">{piece.caption}</p>
          <GateList gates={piece.gateResults} />
          {/*
            O parecer vem DEPOIS dos sete portões e ANTES dos botões, e a ordem
            é a leitura: primeiro o que decide (portão), depois o que informa
            (editor), e só então a decisão de quem aprova. Invertido, o dono
            leria a opinião primeiro e o veredito depois.
          */}
          <EditorialOpinion editorial={piece.editorial} />
          {/*
            O parecer da MÍDIA, num bloco próprio e logo abaixo do de texto.

            Ele existe na tela desde 2026-09-06 (achado F11): a revisão visual
            era produzida, paga e gravada desde o lote 03, e nunca chegava a
            quem decide. Dois blocos, e não um, porque as réguas e as âncoras
            são diferentes: ver `MediaOpinion`.
          */}
          <MediaOpinion editorial={piece.mediaEditorial} />
          <DecisionForm
            piece={piece}
            changeBoxOpen={changeBoxOpen}
            onOpenChangeBox={onOpenChangeBox}
            onCloseChangeBox={onCloseChangeBox}
            rascunho={rascunho}
          />
        </div>
      </div>
    </section>
  );
};
